import React, { useMemo, useState } from 'react';
import { Button, Chip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { getStoryIntegrityData } from './storyIntegrityUtils';

const useStyles = makeStyles(() => ({
  root: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.24)',
    background: 'rgba(7, 12, 28, 0.78)',
  },
  toggle: {
    padding: 0,
    minWidth: 0,
    textTransform: 'none',
    color: '#9ec4ff',
    fontWeight: 800,
    justifyContent: 'flex-start',
  },
  summaryRow: {
    marginTop: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  chip: {
    height: 22,
    fontSize: 11,
    color: '#e5e7ff',
    backgroundColor: 'rgba(77, 121, 255, 0.12)',
    border: '1px solid rgba(77, 121, 255, 0.35)',
  },
  barRow: {
    marginTop: 10,
  },
  barHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 4,
  },
  barTrack: {
    position: 'relative',
    height: 10,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  barFill: {
    position: 'absolute',
    inset: 0,
    width: '0%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, rgba(77,121,255,0.55), rgba(158,196,255,0.95))',
  },
  compositeTrack: {
    position: 'relative',
    marginTop: 12,
    height: 14,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  tauLine: {
    position: 'absolute',
    top: -2,
    bottom: -2,
    width: 2,
    background: '#ffb86b',
    boxShadow: '0 0 10px rgba(255,184,107,0.55)',
  },
  helper: {
    marginTop: 8,
    color: '#9ea4d8',
    fontSize: 11,
    lineHeight: 1.45,
  },
}));

function pct(value) {
  return `${Math.round(Math.max(0, Math.min(1, Number(value) || 0)) * 100)}%`;
}

export default function StoryIntegrityBreakdown({ story, defaultExpanded = false }) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const integrity = useMemo(() => getStoryIntegrityData(story), [story]);

  if (!integrity) return null;

  return (
    <div className={classes.root}>
      <Button className={classes.toggle} onClick={() => setExpanded((value) => !value)}>
        {expanded ? 'Hide scored math' : 'How scored'}
      </Button>

      <div className={classes.summaryRow}>
        <Chip className={classes.chip} size="small" label={`C^S_t ${integrity.composite.toFixed(3)}`} />
        <Chip className={classes.chip} size="small" label={`tau_S ${integrity.tau.toFixed(3)}`} />
        <Chip
          className={classes.chip}
          size="small"
          label={integrity.admissible ? 'Admissible' : 'Below threshold'}
          style={integrity.admissible
            ? { color: '#9ef7c5', borderColor: 'rgba(158,247,197,0.35)' }
            : { color: '#ffb4b4', borderColor: 'rgba(255,120,120,0.35)' }}
        />
      </div>

      {expanded && (
        <>
          {integrity.components.map((item) => (
            <div className={classes.barRow} key={item.key}>
              <div className={classes.barHeader}>
                <Typography variant="body2" style={{ color: '#e5e7ff', fontSize: 12, fontWeight: 700 }}>
                  {item.label}
                </Typography>
                <Typography variant="body2" style={{ color: '#cfd3ff', fontSize: 11 }}>
                  {item.weight.toFixed(2)} × {item.value.toFixed(3)} = {item.contribution.toFixed(3)}
                </Typography>
              </div>
              <div className={classes.barTrack}>
                <div className={classes.barFill} style={{ width: pct(item.value) }} />
              </div>
            </div>
          ))}

          <div className={classes.compositeTrack}>
            <div className={classes.barFill} style={{ width: pct(integrity.composite) }} />
            <div className={classes.tauLine} style={{ left: pct(integrity.tau) }} />
          </div>

          <div className={classes.helper}>
            {`Family ${integrity.family} uses the stored integrity weights and threshold on this story row. The orange line marks tau_S.`}
          </div>
        </>
      )}
    </div>
  );
}
